import React from "react";
import { API } from "@/App";
import { Download, FileCode, Server, LineChart, BookOpen, CheckCircle2, Radio } from "lucide-react";

const MODULES = [
  "Kill Zones (Asie, Londres, NY AM, NY Lunch, NY PM)",
  "Swing High/Low avec marqueurs ^ / v",
  "Tendance du marché (Bullish / Bearish / Range)",
  "PDH / PDL – Previous Daily High/Low",
  "PWH / PWL – Previous Weekly High/Low",
  "PMH / PML – Previous Monthly High/Low",
  "Impulsion institutionnelle (Order Blocks + FVG)",
  "BOS / CHoCH – Break of Structure",
  "Liquidity Sweeps (prises de liquidité)",
  "Alertes Push / Email / Sonores configurables",
];

const PLATFORMS = [
  {
    key: "mt4",
    title: "MetaTrader 4",
    version: "v1.0 · .mq4",
    desc: "L'indicateur ANATRADEX Pro pour MT4. Compatible avec toutes les prop firms MT4 (FTMO, FundedNext, The5ers, MyForexFunds, E8…).",
    icon: Server,
    filename: "ANATRADEX_Pro.mq4",
    install: "MT4 → Fichier → Ouvrir le dossier des données → MQL4/Indicators → coller le fichier → Actualiser le Navigateur",
  },
  {
    key: "mt5",
    title: "MetaTrader 5",
    version: "v1.0 · .mq5",
    desc: "Version MT5 avec groupes d'inputs élégants. Idéale pour les prop firms MT5 (FundingPips, DarwinX, FundedNext Stellar).",
    icon: Server,
    filename: "ANATRADEX_Pro.mq5",
    install: "MT5 → Fichier → Ouvrir le dossier des données → MQL5/Indicators → coller le fichier → Actualiser",
  },
  {
    key: "ctrader",
    title: "cTrader",
    version: "v1.0 · .cs",
    desc: "Indicateur C# à compiler dans cTrader Automate. Compatible avec les prop firms cTrader (TopStep, IC Markets, Pepperstone).",
    icon: FileCode,
    filename: "ANATRADEX_Pro.cs",
    install: "cTrader → Automate → Indicators → New Indicator → coller le code → Build → glisser sur le graphique",
  },
  {
    key: "tradingview",
    title: "TradingView",
    version: "v1.0 · .pine",
    desc: "Script Pine v5 tout-en-un. Fonctionne sur crypto, actions, forex, indices — partout où TradingView affiche un graphique.",
    icon: LineChart,
    filename: "ANATRADEX_Pro.pine",
    install: "TradingView → Pine Editor → coller le code → Ajouter au graphique → Sauvegarder",
  },
];

export default function Downloads() {
  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="font-heading font-bold text-2xl tracking-tight" data-testid="downloads-title">
            Indicateur ANATRADEX Pro
          </h1>
          <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-accent-blue/15 text-accent-blue border border-accent-blue/30">
            v1.0
          </span>
        </div>
        <p className="text-white/60 text-sm max-w-3xl">
          Un seul indicateur, <strong className="text-white">10 modules activables</strong>, pour MT4 / MT5 / cTrader / TradingView.
          Chaque module (Kill Zones, PDH/PWH/PMH, Order Blocks, FVG, BOS, Sweeps…) est <strong className="text-white">optionnel</strong> et
          entièrement <strong className="text-white">personnalisable</strong> : couleur, style de ligne (continue, pointillé, tirets), épaisseur.
        </p>
      </div>

      {/* Modules included */}
      <div className="panel p-5 mb-6" data-testid="downloads-modules">
        <div className="panel-title text-[14px] mb-3 flex items-center gap-2">
          <CheckCircle2 size={16} className="text-accent-green" /> Ce qui est inclus
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 text-sm text-white/70">
          {MODULES.map((m) => (
            <div key={m} className="flex items-start gap-2">
              <CheckCircle2 size={13} className="text-accent-green mt-1 shrink-0" />
              <span>{m}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Platform cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {PLATFORMS.map((p) => (
          <div key={p.key} className="panel p-5 flex flex-col" data-testid={`download-card-${p.key}`}>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-md bg-white/5 grid place-items-center border border-white/10">
                <p.icon size={18} className="text-accent-blue" />
              </div>
              <div className="flex-1">
                <div className="panel-title text-[15px]">{p.title}</div>
                <div className="text-[11px] text-white/40">{p.version}</div>
              </div>
            </div>
            <div className="text-sm text-white/60 mb-3 flex-1">{p.desc}</div>
            <div className="text-[11px] text-white/40 mb-4 font-mono leading-relaxed">
              <span className="text-accent-blue">Installation :</span> {p.install}
            </div>
            <a
              className="btn btn-primary justify-center"
              href={`${API}/downloads/${p.key}`}
              download={p.filename}
              data-testid={`download-btn-${p.key}`}
            >
              <Download size={14} /> Télécharger {p.filename}
            </a>
          </div>
        ))}
      </div>

      {/* Bridge EAs — Phase 2 */}
      <div className="panel p-5 mb-6" data-testid="downloads-bridge-section">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md bg-accent-green/10 grid place-items-center border border-accent-green/30">
            <Radio size={18} className="text-accent-green" />
          </div>
          <div>
            <div className="panel-title text-[15px]">
              ANATRADEX Bridge EA — Synchronisation Prop Firm
              <span className="text-[10px] font-semibold ml-2 px-2 py-0.5 rounded-full bg-accent-green/15 text-accent-green border border-accent-green/30">
                NEW
              </span>
            </div>
            <div className="text-[11px] text-white/40">Connectez vos comptes FTMO / FundedNext / The5%ers / TopStep en temps réel</div>
          </div>
        </div>
        <div className="text-sm text-white/60 mb-4">
          Un Expert Advisor qui tourne 24/7 sur votre plateforme et pousse balance, équité, drawdown
          et trades fermés vers votre dashboard ANATRADEX. Compatible avec toutes les prop firms utilisant MT4, MT5 ou cTrader.
          <br /><span className="text-accent-blue">→ Créez d'abord un compte dans <b>Mes Comptes Live</b> pour obtenir votre token API.</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <a className="btn btn-secondary justify-center" href={`${API}/downloads/bridge_mt4`} download="ANATRADEX_Bridge.mq4"
             data-testid="download-btn-bridge_mt4">
            <Download size={13} /> Bridge MT4 (.mq4)
          </a>
          <a className="btn btn-secondary justify-center" href={`${API}/downloads/bridge_mt5`} download="ANATRADEX_Bridge.mq5"
             data-testid="download-btn-bridge_mt5">
            <Download size={13} /> Bridge MT5 (.mq5)
          </a>
          <a className="btn btn-secondary justify-center" href={`${API}/downloads/bridge_ctrader`} download="ANATRADEX_Bridge.cs"
             data-testid="download-btn-bridge_ctrader">
            <Download size={13} /> Bridge cTrader (.cs)
          </a>
        </div>
      </div>

      {/* Guide */}
      <div className="panel p-5 mb-6" data-testid="downloads-guide-card">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-md bg-white/5 grid place-items-center border border-white/10">
            <BookOpen size={18} className="text-accent-blue" />
          </div>
          <div>
            <div className="panel-title text-[15px]">Guide d'installation complet</div>
            <div className="text-[11px] text-white/40">Instructions détaillées + astuces d'utilisation</div>
          </div>
        </div>
        <div className="text-sm text-white/60 mb-4">
          Guide français complet avec captures d'écran, résolution des erreurs de permissions Windows,
          combinaisons puissantes (confluence PDH+FVG, Sweep+BOS+Kill Zone…), et recommandations par timeframe.
        </div>
        <a
          className="btn btn-secondary"
          href={`${API}/downloads/guide`}
          download="ANATRADEX_Pro_Guide.md"
          data-testid="download-btn-guide"
        >
          <Download size={14} /> Télécharger le guide (ANATRADEX_Pro_Guide.md)
        </a>
      </div>

      {/* Quick tips */}
      <div className="panel p-5 text-sm text-white/70" data-testid="downloads-tips">
        <div className="panel-title text-[14px] mb-2">Astuces express</div>
        <ul className="list-disc list-inside space-y-1 marker:text-accent-blue">
          <li>
            <b>Erreur Windows "vous n'avez pas l'autorisation"</b> : ouvrez MT4/MT5 puis
            <code className="font-mono mx-1 px-1 bg-white/5 rounded">Fichier → Ouvrir le dossier des données</code>
            et collez le fichier dans <code className="font-mono mx-1 px-1 bg-white/5 rounded">MQL4/Indicators</code>.
            Ce dossier <b>bypasse</b> la protection Program Files.
          </li>
          <li>
            Chaque module a un toggle <code className="font-mono">Show...</code> dans les propriétés — activez uniquement ce que vous utilisez pour un graphique clair.
          </li>
          <li>
            <b>Timeframes recommandés</b> : M15 pour day-trading (tous modules), H4/D1 pour swing (désactivez Kill Zones et FVG).
          </li>
          <li>
            <b>Confluence forte</b> : PDH cassé + BOS + Sweep dans une Kill Zone = signal haute probabilité.
          </li>
        </ul>
      </div>
    </div>
  );
}
